import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { IconTile, MonoLabel, focusRingOnDark } from '@/design-system';
import { orderApi, orderKeys } from '@/entities/order';
import { useLogout } from '@/features/auth/hooks';
import { hasWorkspace, useSessionStore } from '@/shared/api';
import { cn, useBreakpoint } from '@/shared/lib';
import { SyncIndicator } from '@/shared/ui';

import { useRailCount } from './rail-counts';
import { RailBrand } from './RailBrand';
import { RailItem } from './RailItem';
import { RailUserBlock } from './RailUserBlock';
import { buildFooterItems, buildNavGroups } from './nav-model';

export interface NavRailProps {
    /**
     * `rail` is de vaste kolom links (228px breed, 190px bij mid).
     * `drawer` is dezelfde inhoud in de schuiflade bij narrow; die krijgt zijn
     * achtergrond, rand, breedte en schaduw van `Drawer`.
     */
    variant?: 'rail' | 'drawer';
    /** Sluit de drawer zodra je ergens heen navigeert. */
    onNavigate?: () => void;
}

/**
 * De navigatierail uit het ontwerp.
 *
 * Donker vlak links, over de volle hoogte: merk bovenaan, daaronder de groepen,
 * en onderaan - vastgepind met `margin-top: auto` - de synchronisatiestatus,
 * Instellingen, Uitloggen en het gebruikersblok.
 *
 * Wat je ziet hangt af van je rol: een inspecteur ziet Mijn dag en geen
 * facturatie of instellingen, een platformbeheerder krijgt de groep PLATFORM
 * erbij. Waar je ook bent, voor dezelfde rol staat er hetzelfde: wat bij één
 * keuring hoort, staat in de kop van die keuring. Zie `nav-model.ts`.
 */
export function NavRail({ variant = 'rail', onNavigate }: NavRailProps) {
    const { level } = useBreakpoint();
    const session = useSessionStore((state) => state.session);
    const impersonation = useSessionStore((state) => state.impersonation);
    const logout = useLogout();

    const workspace = hasWorkspace(session, impersonation);

    useInspectionCount(workspace);
    const groups = useMemo(() => buildNavGroups({ session, workspace }), [session, workspace]);
    const footer = useMemo(() => buildFooterItems(session, workspace), [session, workspace]);

    const fixedWidth = variant === 'rail' && level !== 'narrow';

    return (
        <nav
            aria-label="Hoofdnavigatie"
            className={cn(
                'flex flex-col gap-[18px] overflow-auto px-3 py-[18px]',
                variant === 'rail' &&
                    'border-r border-[var(--rail-border)] bg-[var(--rail-bg)] shrink-0',
                variant === 'drawer' && 'flex-1',
            )}
            style={
                fixedWidth
                    ? {
                          flex: `0 0 var(--rail-w-${level})`,
                          inlineSize: `var(--rail-w-${level})`,
                      }
                    : undefined
            }
        >
            <div className="flex items-center px-2 py-1">
                <RailBrand />
            </div>

            {groups.map((group) => (
                <div
                    key={group.key}
                    role="group"
                    aria-labelledby={`sk-rail-group-${group.key}`}
                    className="flex flex-col gap-[2px]"
                >
                    <MonoLabel
                        id={`sk-rail-group-${group.key}`}
                        size={10}
                        className="px-2 pt-[6px] pb-1 text-[var(--rail-label)]"
                    >
                        {group.label}
                    </MonoLabel>

                    {group.items.map((item) => (
                        <RailItem key={item.key} item={item} onNavigate={onNavigate} />
                    ))}
                </div>
            ))}

            <div className="mt-auto flex flex-col gap-[2px]">
                {/* Het anker van de rondleiding: hier zie je dat de app offline werkt. */}
                <div data-tour="sync">
                    <SyncIndicator surface="onDark" className="w-full justify-start px-[10px]" />
                </div>

                {footer.map((item) => (
                    <RailItem key={item.key} item={item} onNavigate={onNavigate} />
                ))}

                <button
                    type="button"
                    onClick={() => void logout()}
                    className={cn(
                        'group flex cursor-pointer items-center gap-[10px] rounded-[var(--radius-control)]',
                        'py-[5px] pr-[10px] pl-[6px] text-left',
                        'font-[family-name:var(--font-body)] text-[13px] leading-[1.25]',
                        'text-[var(--rail-muted)]',
                        'hover:bg-[var(--rail-item-hover)] hover:text-[var(--rail-item)]',
                        focusRingOnDark,
                    )}
                >
                    <IconTile name="logout" size="sm" surface="dark" />
                    Uitloggen
                </button>

                <RailUserBlock />
            </div>
        </nav>
    );
}

/**
 * Het aantal keuringen naast Keuringen: `meta.total` van `GET /orders`, één
 * verzoek van één regel. Het dashboard leest dezelfde sleutel voor zijn lege
 * staat, dus dat kost geen tweede verzoek. Het scherm Keuringen maakt `['orders', 'counts']`
 * ongeldig na elke wijziging, dus deze teller loopt mee. Zonder omgeving is er
 * niets te tellen; zonder antwoord blijft de teller weg.
 */
function useInspectionCount(workspace: boolean): void {
    const total = useQuery({
        queryKey: orderKeys.total(),
        queryFn: () => orderApi.total(),
        enabled: workspace,
        staleTime: 5 * 60 * 1_000,
    });

    useRailCount('inspections', workspace ? total.data : undefined);
}
